/**
 * Hook pour précharger les mois adjacents (N-1 et N+1)
 * Navigation instantanée vers les mois précédent/suivant
 */

import { useEffect, useRef } from 'react';
import { getMonthRange, addMonths, formatMonthKey } from '../utils/dateTime';
import { cacheGet } from '../utils/cache';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';
import { cacheSet, emitCacheUpdated } from '../utils/cache';
import { mergeAttendance } from '../utils/attendanceMerge';

/**
 * Vérifie si un mois est déjà en cache
 * @param {Date} month - Mois à vérifier
 * @returns {boolean} True si déjà en cache
 */
function isMonthInCache(month) {
  const range = getMonthRange(month);
  const lastFetch = cacheGet('activity/lastFetch', null);
  
  if (!lastFetch || !lastFetch.from || !lastFetch.to) {
    return false;
  }
  
  // Vérifier si le mois est couvert par le cache
  const requestedFrom = new Date(range.from);
  const requestedTo = new Date(range.to);
  const cachedFrom = new Date(lastFetch.from);
  const cachedTo = new Date(lastFetch.to);
  
  return requestedFrom >= cachedFrom && requestedTo <= cachedTo;
}

/**
 * Précharge un mois en background
 * @param {Date} month - Mois à précharger
 * @returns {Promise<boolean>} True si chargé, false si déjà en cache
 */
async function prefetchMonth(month) {
  const range = getMonthRange(month);
  const monthKey = formatMonthKey(month);
  
  // Vérifier si déjà en cache
  if (isMonthInCache(month)) {
    console.log(`🔮 [prefetch] ${monthKey} déjà en cache, skip`);
    return false;
  }
  
  try {
    const userId = localStorage.getItem('userId') || localStorage.getItem('uid') || localStorage.getItem('user_id') || "";
    
    console.log(`🔮 [prefetch] Chargement ${monthKey} (${range.from} → ${range.to})`);
    
    // Charger les opérateurs et attendance en parallèle
    const [operatorsRes, attendanceRes] = await Promise.all([
      fetch(API_ENDPOINTS.OPERATORS(userId), { 
        headers: getAuthHeaders(),
        credentials: 'include'
      }),
      fetch(API_ENDPOINTS.ATTENDANCE_DAYS(range.from, range.to, userId), { 
        headers: getAuthHeaders(),
        credentials: 'include'
      })
    ]);
    
    if (!operatorsRes.ok || !attendanceRes.ok) {
      console.warn(`⚠️ [prefetch] Erreur ${monthKey}`);
      return false;
    }
    
    const operators = await operatorsRes.json();
    const attendance = await attendanceRes.json();
    
    // Normaliser les opérateurs
    const normalizedOperators = operators.map(op => ({
      id: Number(op.id),
      name: op.name || `${op.first_name || ''} ${op.last_name || ''}`.trim() || `Opérateur #${op.id}`
    }));
    
    // Merger avec le cache existant (ne pas écraser tout)
    const existingOperators = cacheGet('activity/operators', []);
    const existingAttendance = cacheGet('activity/attendance', []);
    
    // Merger opérateurs (par ID unique)
    const operatorsMap = new Map();
    existingOperators.forEach(op => operatorsMap.set(op.id, op));
    normalizedOperators.forEach(op => operatorsMap.set(op.id, op));
    const mergedOperators = Array.from(operatorsMap.values());
    
    // Merger attendance avec dédup + tri
    const mergedAttendance = mergeAttendance(existingAttendance, attendance);
    
    // Mettre à jour le cache
    cacheSet('activity/operators', mergedOperators);
    cacheSet('activity/attendance', mergedAttendance);
    
    // Étendre lastFetch pour couvrir ce nouveau mois
    const lastFetch = cacheGet('activity/lastFetch', { from: range.from, to: range.to, at: Date.now() });
    const newFrom = new Date(lastFetch.from) < new Date(range.from) ? lastFetch.from : range.from;
    const newTo = new Date(lastFetch.to) > new Date(range.to) ? lastFetch.to : range.to;
    
    cacheSet('activity/lastFetch', { 
      from: newFrom, 
      to: newTo, 
      at: Date.now() 
    });
    
    emitCacheUpdated('activity/attendance');
    
    console.log(`✅ [prefetch] ${monthKey} chargé (${mergedAttendance.length} rows)`);
    return true;
  } catch (error) {
    console.error(`❌ [prefetch] Erreur ${monthKey}:`, error);
    return false;
  }
}

/**
 * Hook pour précharger automatiquement les mois adjacents
 * @param {Date} currentMonth - Mois actuellement affiché
 */
export function usePrefetchAdjacentMonths(currentMonth) {
  const prefetchedRef = useRef(new Set());
  
  useEffect(() => {
    const monthKey = formatMonthKey(currentMonth);
    
    // Ne précharger qu'une fois par mois
    if (prefetchedRef.current.has(monthKey)) {
      return;
    }
    
    const doPrefetch = async () => {
      // Marquer ce mois comme traité
      prefetchedRef.current.add(monthKey);
      
      // Calculer les mois adjacents
      const prevMonth = addMonths(currentMonth, -1);
      const nextMonth = addMonths(currentMonth, 1);
      
      // Précharger en idle pour ne pas bloquer l'UI
      const prefetchBoth = async () => {
        await Promise.all([
          prefetchMonth(prevMonth),
          prefetchMonth(nextMonth)
        ]);
      };
      
      // Utiliser requestIdleCallback si disponible
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => prefetchBoth(), { timeout: 2000 });
      } else {
        // Fallback: setTimeout
        setTimeout(prefetchBoth, 1000);
      }
    };
    
    doPrefetch();
  }, [currentMonth]);
}
